
class BusinessLicensePaymentFeeDataSource extends MIOObject
{
    private tableView:UITableView = null;

    initWithTableView( tableView:UITableView ) {
        this.tableView = tableView;
    }

    private businessLicense:BusinessLicense = null;
    set business(item:BusinessLicense) {
        this.businessLicense = item;
        this.reloadLines();
    }

    // Fee rows are locked while the license is linked to a payment fee template.
    private _readOnly = false;
    set readOnly(value:boolean) {
        if (this._readOnly == value) return;
        this._readOnly = value;
        this.tableView.reloadData();
    }

    // Matrix index: cell key -> fee row, plus the advanced leftovers.
    private fees:BusinessPaymentFee[] = [];
    private cellMap:{[key:string]:BusinessPaymentFee} = {};
    private advancedFees:BusinessPaymentFee[] = [];

    reloadLines(){
        if (this.businessLicense == null) return;

        let predicate = MIOPredicate.predicateWithFormat('business.identifier == "' + this.businessLicense.identifier + '" and deletedAt == null');
        DBHelper.queryObjectsWithCompletion("BusinessPaymentFee", null, predicate, ["business"], this, function(this:BusinessLicensePaymentFeeDataSource, fees:BusinessPaymentFee[]){
            this.fees = fees;
            this.rebuildIndex();
            this.tableView.reloadData();
        });
    }

    private rebuildIndex(){
        this.cellMap = {};
        this.advancedFees = [];

        for (let fee of this.fees){
            if (fee.deletedAt != null) continue;
            let key = PaymentFeeMatrixCellKeyForLine(fee);
            if (key != null && this.cellMap[key] == null) this.cellMap[key] = fee;
            else this.advancedFees.push(fee);
        }
    }

    numberOfSections(tableview:UITableView){
        if (this.businessLicense == null) return 0;
        return PaymentFeeMatrixSections.length + 1;
    }

    numberOfRowsInSection(tableview:UITableView, section:number){
        if (section < PaymentFeeMatrixSections.length) return PaymentFeeMatrixSections[section].rows.length;
        return this.advancedFees.length;
    }

    titleForHeaderInSection(tableView:UITableView, section:number) {
        if (section < PaymentFeeMatrixSections.length) {
            let def = PaymentFeeMatrixSections[section];
            return MIOLocalizeString(def.titleKey, def.titleDefault);
        }
        if (this.advancedFees.length == 0) return null;
        return MIOLocalizeString("ADVANCED_FEE_RULES", "ADVANCED RULES");
    }

    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){
        if (indexPath.section < PaymentFeeMatrixSections.length) {
            let cell = tableview.dequeueReusableCellWithIdentifier("PaymentFeeMatrixRowCell") as PaymentFeeMatrixRowCell;
            let def = PaymentFeeMatrixSections[indexPath.section].rows[indexPath.row];

            let linesByChannel = {};
            for (let channel of PaymentFeeMatrixChannels) {
                linesByChannel[channel] = this.cellMap[ PaymentFeeMatrixCellKey(def, channel) ];
            }

            cell.delegate = this;
            cell.setRow(def, linesByChannel, this._readOnly);
            return cell;
        }

        let fee = this.advancedFees[indexPath.row];

        // Disabled inputs render too faint to read, so template-managed rows use a
        // label-only cell instead of the editable one with its controls switched off.
        if (this._readOnly) {
            let cell = tableview.dequeueReusableCellWithIdentifier("PaymentFeeReadOnlyCell") as BusinessLicensePaymentFeeReadOnlyCell;
            cell.item = fee;
            return cell;
        }

        let cell = tableview.dequeueReusableCellWithIdentifier("PaymentFeeAdvancedLineCell") as PaymentFeeAdvancedLineCell;
        cell.setLine(fee, false);
        return cell;
    }

    editingStyleForRowAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath) : any {
        if (this._readOnly) return MUITableViewCellEditingStyle.None;
        if (indexPath.section < PaymentFeeMatrixSections.length) return MUITableViewCellEditingStyle.None;
        return MUITableViewCellEditingStyle.Delete;
    }

    commitEditingStyleForRowAtIndexPath(tableView:UITableView, editingStyle:any, indexPath:MIOIndexPath) {
        if (editingStyle != MUITableViewCellEditingStyle.Delete) return;
        if (indexPath.section < PaymentFeeMatrixSections.length) return;

        let fee = this.advancedFees[indexPath.row];
        DBHelper.deleteObjectFromMainContext( fee, true );
        this.reloadLines();
    }

    // A matrix cell changed: both fields empty removes the rule, anything
    // else finds-or-creates the rule for that row + channel.
    matrixCellDidChange(def:PaymentFeeMatrixRowDef, channel:PaymentFeeChannel, fixedDisplay:string, rateDisplay:string){
        if (this._readOnly) return;

        let key = PaymentFeeMatrixCellKey(def, channel);
        let fee = this.cellMap[key];

        let fixed = PaymentFeeMatrixFixedFormat(fixedDisplay);
        let rate = PaymentFeeMatrixRateFormat(rateDisplay);

        if (fixed == null && rate == null) {
            if (fee != null) {
                DBHelper.deleteObjectFromMainContext(fee, false);
                delete this.cellMap[key];
                let index = this.fees.indexOf(fee);
                if (index >= 0) this.fees.splice(index, 1);
            }
            return;
        }

        if (fee == null) {
            let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
            fee = MIOEntityDescription.insertNewObjectForEntityForName("BusinessPaymentFee", ad.managedObjectContext) as BusinessPaymentFee;
            fee.business = this.businessLicense;
            PaymentFeeMatrixApplyDims(fee, def, channel);
            this.cellMap[key] = fee;
            this.fees.push(fee);
        }

        fee.transactionFeeFormat = fixed != null ? fixed : "0";
        fee.feeFormat = rate != null ? rate : "0";
    }

    // Called by the hosting controller's add button: a new ADVANCED rule.
    addAdvancedFee(){
        if (this.businessLicense == null || this._readOnly) return;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let fee = MIOEntityDescription.insertNewObjectForEntityForName( "BusinessPaymentFee", ad.managedObjectContext ) as BusinessPaymentFee;

        fee.business = this.businessLicense;
        fee.paymentEntityType = PaymentEntityType.Adyen;
        fee.cardBrandType = 0;
        fee.fundingSource = 0;
        fee.cardType = 0;
        fee.channel = PaymentFeeChannel.Any;
        fee.threeDSMode = PaymentFeeThreeDSMode.Any;
        fee.has3DS = false;
        fee.transactionFeeFormat = "0";
        fee.feeFormat = "0";

        this.fees.push(fee);
        this.rebuildIndex();
        this.tableView.reloadData();
    }
}

class BusinessLicensePaymentFeeReadOnlyCell extends UITableViewCell
{
    private cardBrandLabel:MUILabel = null;
    private cardCountryLabel:MUILabel = null;
    private fundingSourceLabel:MUILabel = null;
    private cardTypeLabel:MUILabel = null;
    private channelLabel:MUILabel = null;
    private threeDSLabel:MUILabel = null;
    private transactionFeeLabel:MUILabel = null;
    private feeLabel:MUILabel = null;

    awakeFromHTML(): void {
        this.cardBrandLabel = MUIOutlet(this, "card-brand-lbl", "MUILabel");
        this.cardCountryLabel = MUIOutlet(this, "card-country-lbl", "MUILabel");
        this.fundingSourceLabel = MUIOutlet(this, "funding-source-lbl", "MUILabel");
        this.cardTypeLabel = MUIOutlet(this, "card-type-lbl", "MUILabel");
        this.channelLabel = MUIOutlet(this, "channel-lbl", "MUILabel");
        this.threeDSLabel = MUIOutlet(this, "three-ds-lbl", "MUILabel");
        this.transactionFeeLabel = MUIOutlet(this, "transaction-fee-lbl", "MUILabel");
        this.feeLabel = MUIOutlet(this, "fee-lbl", "MUILabel");

        this.selectionStyle = UITableViewCellSelectionStyle.None;
    }

    set item(item:BusinessPaymentFee) {
        this.cardBrandLabel.text = BusinessPaymentFee.cardBrandTypeString( item.cardBrandType );
        this.cardCountryLabel.text = item.cardCountry != null && item.cardCountry.length > 0 ? item.cardCountry : MIOLocalizeString("ALL", "ALL");
        this.fundingSourceLabel.text = BusinessPaymentFee.fundingSourceString( item.fundingSource );
        this.cardTypeLabel.text = BusinessPaymentFee.cardTypeString( item.cardType );
        this.channelLabel.text = BusinessPaymentFee.channelString( item.channel );
        this.threeDSLabel.text = BusinessPaymentFee.threeDSModeString( item.threeDSMode );
        this.transactionFeeLabel.text = item.transactionFeeFormat != null ? item.transactionFeeFormat : "0";
        this.feeLabel.text = item.feeFormat != null ? item.feeFormat : "0";
    }
}
